import React, { useState, useEffect } from "react";
import {
  Modal,
  Icon,
  ModalOverlay,
  useToast,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  SimpleGrid,
  ModalFooter,
  Button,
  Checkbox,
  Stack,
} from "@chakra-ui/react";
import axios from "axios";
import { FaEdit, FaTrash, FaClipboardList } from "react-icons/fa"; // Import the icons from react-icons
import { BASE_URL } from "../../Globalapi/Globalapi";

function EditModalSubject({ staffId, onClose }) {
  const toast = useToast();

  const [subjects, setSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [staffName, setStaffName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [teachersSubjects, setTeachersSubjects] = useState([]); // Track subjects already assigned

  useEffect(() => {
    setIsLoading(true);

    // Fetch all subjects
    axios
      .get(`${BASE_URL}/admin/subject/list_subject.php`)
      .then((response) => {
        setSubjects(response.data.data); // Assuming response.data.data is the list of subjects
      })
      .catch((error) => {
        console.error("Error fetching subjects:", error);
      });

    // Fetch selected subjects for the teacher
    axios
      .get(
        `${BASE_URL}/admin/subject/selected_teacher_subject.php?staff_id=${staffId}`
      )
      .then((response) => {
        if (Array.isArray(response.data.selectedSubjects)) {
          const selectedSubjectIds = response.data.selectedSubjects.map(
            (subject) => subject.subject_id
          );
          setTeachersSubjects(response.data.selectedSubjects); // Store full subject details for teacher
          setSelectedSubjects(selectedSubjectIds); // Store just the subject ids
          setStaffName(response.data.selectedSubjects[0]?.staffName || "Staff");
        } else {
          console.error("Unexpected response structure", response.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching selected subjects:", error);
      })
      .finally(() => setIsLoading(false));
  }, [staffId]);

  const handleCheckboxChange = (subjectId) => {
    setSelectedSubjects((prevSelected) =>
      prevSelected.includes(subjectId)
        ? prevSelected.filter((id) => id !== subjectId)
        : [...prevSelected, subjectId]
    );
  };

  const handleSubmit = () => {
    // Log the selected subjects array to see which subjects are selected
    console.log("Selected subject IDs:", selectedSubjects);

    // Prepare updated subjects for sending to the server
    const updatedSubjects = {
      id: "someUniqueId", // This could be a unique identifier for the action (can be passed as a prop or calculated)
      staff_id: staffId,
      subject_ids: selectedSubjects, // The array of selected subject IDs
    };

    // Log the updated subjects array before sending to the server
    console.log("Updated subjects to be submitted:", updatedSubjects);

    // Ensure the array is not empty before making the PUT request
    if (updatedSubjects.subject_ids.length === 0) {
      toast({
        title: "No Subject Selected",
        description: "Please select at least one subject.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Send the updated subjects as an array to the server
    axios
      .put(
        `${BASE_URL}/admin/subject/edit_subject_to_teacher.php`,
        updatedSubjects
      )
      .then((response) => {
        console.log("Server Response:", response.data);

        if (response.data.success) {
          toast({
            title: "Subjects Updated Successfully",
            description: "The subjects have been updated successfully.",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
          onClose(); // Close the modal after saving
        } else {
          toast({
            title: "Failed to Update Subjects",
            description: "Failed to update subjects: " + response.data.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      })
      .catch((error) => {
        console.error("Error updating subjects:", error);
        toast({
          title: "Failed to Update Subjects",
          description: "An error occurred while updating subjects.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="lg"
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        borderRadius="lg"
        boxShadow="xl"
        bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
        color="gray.700"
        p={6}
        maxW="700px"
        maxH="550px"
        overflowY="auto"
      >
        <ModalHeader
          fontSize="xl"
          fontWeight="bold"
          textAlign="center"
          color="teal.600"
        >
          {" "}
          <Icon as={FaEdit} boxSize={5} mr={2} />
          Edit Subjects for {staffName}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          overflowY="auto"
          px={4}
          css={{
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(255, 255, 255, 0.2)",
              borderRadius: "10px",
              backdropFilter: "blur(8px)",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(173, 216, 230, 0.6)",
              borderRadius: "10px",
              backdropFilter: "blur(8px)",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "rgba(173, 216, 230, 0.9)",
            },
          }}
        >
          <Stack spacing={4}>
            {subjects.length > 0 ? (
              <SimpleGrid columns={2} spacing={4}>
                {subjects.map((subject) => (
                  <Checkbox
                    key={subject.id}
                    isChecked={selectedSubjects.includes(subject.id)}
                    onChange={() => handleCheckboxChange(subject.id)}
                  >
                    {subject.subject}
                  </Checkbox>
                ))}
              </SimpleGrid>
            ) : (
              <div>No subjects available</div>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            bgGradient="linear(to-r, teal.400, teal.600)"
            color="white"
            _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
            mr={3}
            onClick={handleSubmit}
          >
            Save
          </Button>
          {/* <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button> */}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default EditModalSubject;
